
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, Settings, CreditCard, LogOut, Building, Lock } from "lucide-react";
import logo from "@/assets/logo.png";
import { useQuery } from "@tanstack/react-query";
import { getOrgForUser } from "@/lib/orgBilling";
import { useToast } from "@/hooks/use-toast";

export function AdminHeader() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const [userEmail, setUserEmail] = useState<string>("");
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [isImageReady, setIsImageReady] = useState(false);
    const lastLogoPathRef = useRef<string | null>(null);

    useEffect(() => {
        const getUser = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                setUserEmail(user.email || "");
            }
        };
        getUser();
    }, []);

    const { data: org } = useQuery({
        queryKey: ["admin-header-org"],
        queryFn: () => getOrgForUser(supabase),
    });

    // Check plan key (requires Starter or Growth for Abuse)
    const isPlanPaid = org?.plan_key === 'starter' || org?.plan_key === 'growth';

    // Preload logo to avoid flicker
    useEffect(() => {
        const loadLogo = async () => {
            const path = org?.logo_path;

            // If path is null/undefined, reset to generic
            if (!path) {
                setLogoUrl(null);
                setIsImageReady(false);
                lastLogoPathRef.current = null;
                return;
            }

            // If path hasn't changed, do nothing (preserve existing loaded state)
            if (path === lastLogoPathRef.current && logoUrl) return;

            lastLogoPathRef.current = path;
            // Don't necessarily reset isImageReady to false if we have a stale url that is "okay" to show? 
            // But usually we want to show loading for the NEW logo.
            setIsImageReady(false);

            const { data } = await supabase.storage.from("org-assets").createSignedUrl(path, 60 * 60);
            if (data?.signedUrl) {
                // Preload
                const img = new Image();
                img.src = data.signedUrl;
                img.onload = () => {
                    // Check if effective path is still the same (avoid race condition)
                    if (lastLogoPathRef.current === path) {
                        setLogoUrl(data.signedUrl);
                        setIsImageReady(true);
                    }
                };
            }
        };
        loadLogo();
    }, [org?.logo_path]);

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate("/admin");
    };

    const handleAbuseClick = (e: React.MouseEvent) => {
        if (!isPlanPaid) {
            e.preventDefault();
            toast({
                title: "Upgrade Required",
                description: "Abuse prevention is available on Starter and Growth plans.",
                variant: "destructive",
                action: (
                    <Button variant="outline" size="sm" onClick={() => window.location.assign("/admin/settings?tab=billing")}>
                        Upgrade
                    </Button>
                ),
            });
        }
    };

    return (
        <header className="sticky top-0 z-50 w-full border-b border-gray-200 dark:border-gray-800 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                <div className="flex items-center gap-8">
                    <Link to="/admin/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                        <div className="bg-primary/10 p-1 rounded-lg">
                            <img src={logo} alt="RaffleKit" className="w-6 h-6 rounded-md" />
                        </div>
                        <h1 className="font-heading text-lg font-bold tracking-tight">RaffleKit <span className="text-muted-foreground font-normal">Admin</span></h1>
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        <NavLink
                            to="/admin/dashboard"
                            className={({ isActive }) => cn(
                                "text-sm font-medium transition-all px-3 py-2 rounded-md hover:bg-accent hover:text-accent-foreground",
                                isActive ? "bg-secondary text-secondary-foreground shadow-sm" : "text-muted-foreground"
                            )}
                        >
                            Contests
                        </NavLink>

                        <NavLink
                            to={isPlanPaid ? "/admin/analytics" : "#"}
                            onClick={(e) => {
                                if (!isPlanPaid) {
                                    e.preventDefault();
                                    toast({
                                        title: "Upgrade Required",
                                        description: "Analytics is available on Starter and Growth plans.",
                                        variant: "destructive",
                                        action: (
                                            <Button variant="outline" size="sm" onClick={() => window.location.assign("/admin/settings?tab=billing")}>
                                                Upgrade
                                            </Button>
                                        ),
                                    });
                                }
                            }}
                            className={({ isActive }) => cn(
                                "text-sm font-medium transition-all px-3 py-2 rounded-md flex items-center gap-2",
                                isPlanPaid ? "hover:bg-accent hover:text-accent-foreground" : "opacity-60 cursor-not-allowed hover:bg-transparent",
                                isActive && isPlanPaid ? "bg-secondary text-secondary-foreground shadow-sm" : "text-muted-foreground"
                            )}
                        >
                            {!isPlanPaid && <Lock className="w-3 h-3" />}
                            Analytics
                        </NavLink>

                        <NavLink
                            to={isPlanPaid ? "/admin/abuse" : "#"}
                            onClick={handleAbuseClick}
                            className={({ isActive }) => cn(
                                "text-sm font-medium transition-all px-3 py-2 rounded-md flex items-center gap-2",
                                isPlanPaid ? "hover:bg-accent hover:text-accent-foreground" : "opacity-60 cursor-not-allowed hover:bg-transparent",
                                isActive && isPlanPaid ? "bg-secondary text-secondary-foreground shadow-sm" : "text-muted-foreground"
                            )}
                        >
                            {!isPlanPaid && <Lock className="w-3 h-3" />}
                            Abuse
                        </NavLink>
                    </nav>
                </div>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="relative h-auto py-1.5 pl-4 pr-1.5 rounded-full flex items-center gap-3 hover:bg-accent/50 transition-colors data-[state=open]:bg-accent/50 border border-transparent hover:border-border/40">
                            <span className="text-sm font-medium text-muted-foreground">Settings</span>
                            <Avatar className="h-8 w-8 border border-border/50 shadow-sm">
                                {logoUrl && isImageReady && (
                                    <AvatarImage
                                        src={logoUrl}
                                        alt={org?.name || "Org"}
                                        className="object-cover animate-in fade-in duration-300"
                                    />
                                )}
                                <AvatarFallback className="bg-muted">
                                    {org?.logo_path && !isImageReady ? (
                                        <div className="w-full h-full bg-slate-200 animate-pulse rounded-full" />
                                    ) : (
                                        <User className="h-4 w-4 opacity-50" />
                                    )}
                                </AvatarFallback>
                            </Avatar>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56" align="end" forceMount>
                        <DropdownMenuLabel className="font-normal">
                            <div className="flex flex-col space-y-1">
                                <p className="text-sm font-medium leading-none">{org?.name || "Admin"}</p>
                                <p className="text-xs leading-none text-muted-foreground">
                                    {userEmail}
                                </p>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                            <Link to="/admin/settings?tab=profile" className="cursor-pointer w-full flex items-center">
                                <User className="mr-2 h-4 w-4" />
                                <span>Profile</span>
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                            <Link to="/admin/settings?tab=org" className="cursor-pointer w-full flex items-center">
                                <Building className="mr-2 h-4 w-4" />
                                <span>Organization</span>
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                            <Link to="/admin/settings?tab=billing" className="cursor-pointer w-full flex items-center">
                                <CreditCard className="mr-2 h-4 w-4" />
                                <span>Billing</span>
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600 cursor-pointer">
                            <LogOut className="mr-2 h-4 w-4" />
                            <span>Log out</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    );
}
